/**
 * AI 답의 마크다운을 HTML로 바꾼다. 라이브러리 없이 자주 쓰는 문법만 다룬다.
 * - 먼저 글 전체를 HTML 이스케이프한 뒤 우리 태그만 붙인다. 그래서 답 안의 HTML은 글자로 보인다
 * - 링크는 http(s)만 연다
 * - 다룸: 코드 블록, 인라인 코드, 제목, 굵게·기울임·취소선, 링크, 목록(중첩 1단계), 인용, 표, 구분선, 문단
 */
export function renderNewplaAiMarkdown(src: string): string {
  const codeBlocks: string[] = [];
  let text = escapeHtml((src ?? '').replace(/\r\n?/g, '\n'));

  // 코드 블록은 안쪽을 건드리지 않게 먼저 빼 둔다
  text = text.replace(/```([\w+#.-]*)[^\n]*\n([\s\S]*?)(?:```|$)/g, (_m, lang: string, code: string) => {
    const label = lang || 'code';
    codeBlocks.push(
      `<div class="np-ai-code"><div class="np-ai-code-head"><span>${label}</span>` +
      `<span class="np-ai-code-copy" role="button" title="코드 복사"><i class="bi bi-clipboard"></i> 복사</span></div>` +
      `<pre><code>${code.replace(/\n$/, '')}</code></pre></div>`);
    return `\u0000CODE${codeBlocks.length - 1}\u0000`;
  });

  const lines = text.split('\n');
  const out: string[] = [];
  let para: string[] = [];
  let i = 0;

  const flushPara = () => {
    if (para.length) {
      out.push(`<p>${para.map(inline).join('<br>')}</p>`);
      para = [];
    }
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (/^\u0000CODE\d+\u0000$/.test(trimmed)) {
      flushPara();
      out.push(trimmed);
      i++;
      continue;
    }
    if (!trimmed) {
      flushPara();
      i++;
      continue;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (heading) {
      flushPara();
      const level = Math.min(heading[1].length + 2, 6); // 답 안의 제목은 화면 제목보다 작게
      out.push(`<h${level}>${inline(heading[2])}</h${level}>`);
      i++;
      continue;
    }
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      flushPara();
      out.push('<hr>');
      i++;
      continue;
    }
    if (/^&gt;\s?/.test(trimmed)) {
      flushPara();
      const quote: string[] = [];
      while (i < lines.length && /^&gt;\s?/.test(lines[i].trim())) {
        quote.push(lines[i].trim().replace(/^&gt;\s?/, ''));
        i++;
      }
      out.push(`<blockquote>${quote.map(inline).join('<br>')}</blockquote>`);
      continue;
    }
    if (isTableStart(lines, i)) {
      flushPara();
      const header = splitRow(lines[i]);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      out.push('<div class="np-ai-table"><table><thead><tr>' + header.map(c => `<th>${inline(c)}</th>`).join('') +
        '</tr></thead><tbody>' + rows.map(r => '<tr>' + r.map(c => `<td>${inline(c)}</td>`).join('') + '</tr>').join('') +
        '</tbody></table></div>');
      continue;
    }
    if (listItem(line)) {
      flushPara();
      const { html, next } = renderList(lines, i);
      out.push(html);
      i = next;
      continue;
    }
    para.push(trimmed);
    i++;
  }
  flushPara();

  return out.join('\n').replace(/\u0000CODE(\d+)\u0000/g, (_m, n: string) => codeBlocks[+n]);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function inline(s: string): string {
  const codes: string[] = [];
  let t = s.replace(/`([^`]+)`/g, (_m, c: string) => {
    codes.push(`<code>${c}</code>`);
    return `\u0001${codes.length - 1}\u0001`;
  });
  t = t.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  t = t.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  t = t.replace(/(^|[^*])\*([^*\s][^*]*)\*/g, '$1<em>$2</em>');
  t = t.replace(/~~([^~]+)~~/g, '<del>$1</del>');
  return t.replace(/\u0001(\d+)\u0001/g, (_m, n: string) => codes[+n]);
}

interface ListMatch { indent: number; ordered: boolean; text: string; }

function listItem(line: string): ListMatch | null {
  const m = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/.exec(line);
  return m ? { indent: m[1].length, ordered: /\d/.test(m[2]), text: m[3] } : null;
}

function renderList(lines: string[], start: number): { html: string; next: number } {
  const first = listItem(lines[start])!;
  const tag = first.ordered ? 'ol' : 'ul';
  const items: string[] = [];
  let i = start;
  while (i < lines.length) {
    const item = listItem(lines[i]);
    if (!item || item.indent < first.indent) {
      break;
    }
    if (item.indent > first.indent) {
      // 한 단계 안쪽 목록은 앞 항목에 붙인다
      const nested = renderList(lines, i);
      if (items.length) {
        items[items.length - 1] = items[items.length - 1].replace(/<\/li>$/, nested.html + '</li>');
      }
      i = nested.next;
      continue;
    }
    items.push(`<li>${inline(item.text)}</li>`);
    i++;
  }
  return { html: `<${tag}>${items.join('')}</${tag}>`, next: i };
}

function isTableStart(lines: string[], i: number): boolean {
  return i + 1 < lines.length && /^\s*\|.*\|\s*$/.test(lines[i]) && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1]);
}

function splitRow(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
}
